
import { Box, Text, Image, Button, HStack } from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { PageBlock } from '@/types/pageBuilder'

interface BlockPreviewProps {
  block: PageBlock
}

export function BlockPreview({ block }: BlockPreviewProps) {
  const colors = useThemeColors()

  switch (block.type) {
    case 'heading':
      const HeadingTag = `h${block.content.level || 2}` as keyof JSX.IntrinsicElements
      return (
        <HeadingTag style={{ margin: 0, fontSize: `${24 - (block.content.level || 2) * 2}px` }}>
          {block.content.text || 'Heading'}
        </HeadingTag>
      )

    case 'text':
      return (
        <Text fontSize="sm" color={colors.textMuted} noOfLines={2}>
          {block.content.text || 'Text content...'}
        </Text>
      )

    case 'image':
      return block.content.url ? (
        <Box>
          <Image
            src={block.content.url}
            alt={block.content.alt || ''}
            maxH="100px"
            objectFit="contain"
            borderRadius="md"
          />
          {block.content.caption && (
            <Text fontSize="xs" color={colors.textMuted} mt={1}>
              {block.content.caption}
            </Text>
          )}
        </Box>
      ) : (
        <Text fontSize="sm" color={colors.textMuted} fontStyle="italic">
          No image URL provided
        </Text>
      )

    case 'button':
      return (
        <Button size="sm" colorScheme={block.content.style === 'primary' ? 'blue' : 'gray'} variant={block.content.style === 'outline' ? 'outline' : 'solid'}>
          {block.content.text || 'Button'}
        </Button>
      )

    case 'spacer':
      return (
        <Box h={`${block.content.height || 40}px`} bg={colors.sectionBg} borderRadius="sm" border="1px dashed" borderColor={colors.border}>
          <Text fontSize="xs" color={colors.textMuted} textAlign="center" pt={2}>
            Spacer ({block.content.height || 40}px)
          </Text>
        </Box>
      )

    case 'divider':
      return (
        <Box borderTop="1px" borderColor={colors.border} borderStyle={block.content.style || 'solid'} py={2}>
          <Text fontSize="xs" color={colors.textMuted} textAlign="center">
            Divider
          </Text>
        </Box>
      )

    case 'video':
      return block.content.url ? (
        <Box>
          <Box
            as="video"
            src={block.content.url}
            controls={block.content.controls !== false}
            autoPlay={block.content.autoplay || false}
            maxH="150px"
            borderRadius="md"
            w="100%"
          />
          {block.content.caption && (
            <Text fontSize="xs" color={colors.textMuted} mt={1}>
              {block.content.caption}
            </Text>
          )}
        </Box>
      ) : (
        <Text fontSize="sm" color={colors.textMuted} fontStyle="italic">
          No video URL provided
        </Text>
      )

    case 'quote':
      return (
        <Box
          borderLeft="4px"
          borderColor="blue.500"
          pl={4}
          py={2}
          bg={colors.sectionBg}
          borderRadius="md"
        >
          <Text fontSize="md" fontStyle="italic" color={colors.text} mb={2}>
            "{block.content.text || 'Quote text'}"
          </Text>
          {block.content.author && (
            <Text fontSize="xs" color={colors.textMuted}>
              — {block.content.author}
            </Text>
          )}
        </Box>
      )

    case 'list':
      const ListTag = block.content.type === 'ol' ? 'ol' : 'ul'
      const items = Array.isArray(block.content.items) ? block.content.items : []
      return (
        <Box as={ListTag} pl={4} color={colors.text}>
          {items.length > 0 ? (
            items.map((item: string, index: number) => (
              <Box as="li" key={index} fontSize="sm" mb={1}>
                {item}
              </Box>
            ))
          ) : (
            <Box as="li" fontSize="sm" color={colors.textMuted} fontStyle="italic">
              No items yet
            </Box>
          )}
        </Box>
      )

    case 'code':
      return (
        <Box
          bg={colors.sectionBg}
          p={3}
          borderRadius="md"
          border="1px"
          borderColor={colors.border}
          fontFamily="mono"
          fontSize="xs"
          overflowX="auto"
        >
          <Text color={colors.textMuted} fontSize="xs" mb={1}>
            {block.content.language || 'javascript'}
          </Text>
          <Text color={colors.text} whiteSpace="pre-wrap">
            {block.content.code || '// Enter your code here...'}
          </Text>
        </Box>
      )

    case 'html':
      return (
        <Box
          bg={colors.sectionBg}
          p={3}
          borderRadius="md"
          border="1px"
          borderColor={colors.border}
          fontSize="xs"
        >
          <Text color={colors.textMuted} fontSize="xs" mb={2}>
            HTML Block
          </Text>
          <Text color={colors.text} fontFamily="mono" whiteSpace="pre-wrap" noOfLines={3}>
            {block.content.html || '<!-- Enter your HTML here -->'}
          </Text>
        </Box>
      )

    case 'columns':
      const columnCount = block.content.columns || 2
      return (
        <Box
          border="2px dashed"
          borderColor={colors.border}
          borderRadius="md"
          p={4}
          bg={colors.sectionBg}
        >
          <Text fontSize="sm" color={colors.textMuted} mb={2} textAlign="center">
            {columnCount} Column Layout
          </Text>
          <HStack spacing={2}>
            {Array.from({ length: columnCount }).map((_, i) => (
              <Box
                key={i}
                flex="1"
                h="60px"
                bg={colors.cardBg}
                border="1px"
                borderColor={colors.border}
                borderRadius="sm"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text fontSize="xs" color={colors.textMuted}>
                  Column {i + 1}
                </Text>
              </Box>
            ))}
          </HStack>
        </Box>
      )

    default:
      return <Text fontSize="sm" color={colors.textMuted}>Unknown block type</Text>
  }
}

